import { Input, InputElement } from "@/components/ui/input";
import { useTheme } from "@/libs/theme";
import { cn } from "@/utils/ui";
import {
  CookieIcon,
  EclipseIcon,
  EllipsisVerticalIcon,
  HandshakeIcon,
  MoonIcon,
  PaletteIcon,
  SearchIcon,
  SmileIcon,
  SunIcon,
  SunMoonIcon,
} from "lucide-react";
import { AnimatedLogo } from "../animated-logo";
import { AuthLoader } from "../auth-loader";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { AuthActions } from "./auth-actions";
import { Navigation } from "./navigation";
import { UserActions } from "./user-actions";

export function AppHeader({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme();

  return (
    <header
      className={cn(
        "px-2 border-b h-18 sm:px-4 lg:px-6 border-gray-border-subtle bg-gray-app-bg",
        className,
      )}
    >
      <div className="flex justify-between h-full">
        <div className="flex items-center shrink-0">
          <AnimatedLogo className="block size-10" />
        </div>

        <Navigation className="ml-8 mr-12" />

        <div className="flex items-center flex-1">
          <div className="relative sm:w-xs md:w-sm lg:w-md xl:w-lg 2xl:w-xl">
            <InputElement>
              <SearchIcon className="size-5" />
            </InputElement>
            <Input
              className="h-10 xl:max-w-md 2xl:max-w-lg ps-12"
              type="search"
              placeholder="搜点素材"
              variant="soft"
            />
          </div>
        </div>

        <div className="items-center hidden sm:flex">
          <AuthLoader
            renderLoading={AuthActions}
            renderUnauthenticated={AuthActions}
            renderError={() => "用户信息损坏，刷新页面重试"}
          >
            {({ data }) => <UserActions currentUser={data.user} />}
          </AuthLoader>
        </div>

        <div className="flex items-center ml-4 -mr-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                intent="plain"
                size="icon"
                className="rounded-full text-gray-fg-secondary"
              >
                <EllipsisVerticalIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <PaletteIcon />
                  更换主题
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent>
                    <DropdownMenuRadioGroup
                      value={theme}
                      onValueChange={(value) => setTheme(value)}
                    >
                      <DropdownMenuRadioItem value="moonrise">
                        <svg
                          className="size-8 -m-2 text-accent-solid"
                          xmlns="http://www.w3.org/2000/svg"
                          width="32"
                          height="32"
                          viewBox="0 0 512 512"
                        >
                          <title>MoonRise Icon</title>
                          <defs>
                            <linearGradient
                              id="meteoconsMoonriseFill0"
                              x1="54.3"
                              x2="187.2"
                              y1="29"
                              y2="259.1"
                              gradientUnits="userSpaceOnUse"
                            >
                              <stop offset="0" stop-color="currentColor" />
                              <stop offset=".5" stop-color="currentColor" />
                              <stop offset="1" stop-color="currentColor" />
                            </linearGradient>
                            <clipPath id="meteoconsMoonriseFill1">
                              <path
                                fill="none"
                                d="M512 306H304l-35.9-31.4a18.4 18.4 0 0 0-24.2 0L208 306H0V0h512Z"
                              />
                            </clipPath>
                            <symbol
                              id="meteoconsMoonriseFill2"
                              viewBox="0 0 270 270"
                            >
                              <path
                                fill="url(#meteoconsMoonriseFill0)"
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="6"
                                d="M252.3 168.6A133.4 133.4 0 0 1 118 36.2A130.5 130.5 0 0 1 122.5 3A133 133 0 0 0 3 134.6C3 207.7 63 267 137.2 267c62.5 0 114.8-42.2 129.8-99.2a135.6 135.6 0 0 1-14.8.8Z"
                              >
                                <animateTransform
                                  additive="sum"
                                  attributeName="transform"
                                  dur="6s"
                                  repeatCount="indefinite"
                                  type="rotate"
                                  values="-15 135 135; 9 135 135; -15 135 135"
                                />
                              </path>
                            </symbol>
                          </defs>
                          <path
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="18"
                            d="M128 332h88l40-36l40 36h88"
                            style={{
                              stroke: "var(--gray-border)",
                            }}
                          />
                          <g clip-path="url(#meteoconsMoonriseFill1)">
                            <use
                              width="270"
                              height="270"
                              href="#meteoconsMoonriseFill2"
                              transform="translate(121 121)"
                            />
                          </g>
                        </svg>
                        月升
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="system">
                        <SunMoonIcon />
                        跟随系统
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="dark">
                        <MoonIcon />
                        暗色
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="light">
                        <SunIcon />
                        亮色
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem>
                  <EclipseIcon />
                  关于月球像素社区
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <SmileIcon />
                  使用帮助
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem>
                  <HandshakeIcon />
                  用户协议
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <CookieIcon />
                  隐私政策
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
